import React, { useEffect, useState } from 'react';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import { useNavigate } from 'react-router-dom';
import { Briefcase, FileText, Clock, CheckCircle2, BarChart3, Gavel, ArrowRight } from 'lucide-react';

export default function VendorDashboard() {
  const { vendorUser, vendorFetch } = useVendorAuth();
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const [rfqCount, setRfqCount] = useState(0);
  const [invCount, setInvCount] = useState(0);
  const [auctionCount, setAuctionCount] = useState(0);

  useEffect(() => {
    vendorFetch('/partner/application').then(setApp).catch(() => {});
    if (vendorUser?.vendor_id) {
      vendorFetch('/partner/rfqs').then(d => setRfqCount(d.total || 0)).catch(() => {});
      vendorFetch('/partner/invoices').then(d => setInvCount(d.total || 0)).catch(() => {});
      vendorFetch('/partner/auctions').then(d => setAuctionCount(d.total || 0)).catch(() => {});
    }
  }, [vendorUser]);

  const isApproved = vendorUser?.vendor_id;
  const STATUS_COLOR = {
    draft: 'text-slate-600 bg-slate-100',
    submitted: 'text-blue-700 bg-blue-50',
    under_review: 'text-amber-700 bg-amber-50',
    approved: 'text-emerald-700 bg-emerald-50',
    rejected: 'text-red-600 bg-red-50',
    clarification_needed: 'text-orange-700 bg-orange-50',
  };

  return (
    <div className="space-y-6" data-testid="vendor-dashboard">
      <div>
        <h1 className="font-bold text-2xl text-slate-900">Welcome, {vendorUser?.name}</h1>
        <p className="text-sm text-slate-500 mt-0.5">{vendorUser?.company_name} · Partner Portal</p>
      </div>

      {/* Application status banner */}
      {app && !isApproved && (
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="font-bold text-slate-900">Application Status</div>
              <div className="text-xs text-slate-500 mt-0.5">Complete and submit your pre-qualification questionnaire to be empanelled.</div>
            </div>
            <span className={`text-[11px] font-bold uppercase px-2.5 py-1 rounded-sm ${STATUS_COLOR[app.status] || ''}`}>{app.status?.replace(/_/g, ' ')}</span>
          </div>
          {['draft', 'clarification_needed'].includes(app.status) && (
            <button onClick={() => nav('/partner/application')}
              className="mt-3 flex items-center gap-1.5 text-xs font-bold text-[#3b4872] hover:underline">
              {app.status === 'draft' ? 'Continue Application →' : 'Respond to Clarifications →'}
            </button>
          )}
        </div>
      )}

      {/* Quick stats (only if approved) */}
      {isApproved && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { icon: Briefcase, label: 'Open RFQs', value: rfqCount, link: '/partner/dashboard/rfqs' },
            { icon: FileText, label: 'Invoices', value: invCount, link: '/partner/dashboard/invoices' },
            { icon: Gavel, label: 'Auctions', value: auctionCount, link: '/partner/dashboard/auctions' },
          ].map((s) => (
            <button key={s.label} onClick={() => nav(s.link)}
              className="bg-white border border-slate-200 rounded-sm p-4 text-left hover:border-[#3b4872]/30 transition-colors">
              <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold mb-2"><s.icon size={13} />{s.label}</div>
              <div className="text-2xl font-extrabold text-[#3b4872]">{s.value}</div>
            </button>
          ))}
        </div>
      )}

      {/* Quick links */}
      <div className="grid sm:grid-cols-2 gap-3">
        {[
          { title: 'My Profile', desc: 'View and update company details', link: '/partner/dashboard/profile', icon: CheckCircle2 },
          { title: 'My RFQs', desc: 'View invitations and submit quotes', link: '/partner/dashboard/rfqs', icon: Briefcase, locked: !isApproved },
          { title: 'Invoice Upload', desc: 'Submit invoices for payment', link: '/partner/dashboard/invoices', icon: FileText, locked: !isApproved },
          { title: 'Ledger', desc: 'POs, payments, and outstanding', link: '/partner/dashboard/ledger', icon: BarChart3, locked: !isApproved },
          { title: 'Performance', desc: 'Your delivery and quality scores', link: '/partner/dashboard/performance', icon: BarChart3, locked: !isApproved },
          { title: 'Auctions', desc: 'Join live reverse auctions', link: '/partner/dashboard/auctions', icon: Gavel, locked: !isApproved },
        ].map((item) => (
          <button key={item.title} onClick={() => !item.locked && nav(item.link)} disabled={item.locked}
            className={`flex items-center gap-4 bg-white border border-slate-200 rounded-sm p-4 text-left transition-colors
              ${item.locked ? 'opacity-50 cursor-not-allowed' : 'hover:border-[#3b4872]/30 hover:shadow-sm'}`}>
            <div className="w-9 h-9 bg-[#3b4872]/10 rounded-sm flex items-center justify-center shrink-0">
              <item.icon size={16} className="text-[#3b4872]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-slate-900">{item.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">{item.locked ? 'Available after application approval' : item.desc}</div>
            </div>
            {!item.locked && <ArrowRight size={15} className="text-slate-400 shrink-0" />}
          </button>
        ))}
      </div>
    </div>
  );
}
